import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import ClearFilterBtn from "../../../application/ClearFilterBtn";

import Highcharts from 'highcharts/highstock';

import useDataSource from "../../../../hooks/useDataSource";

import {
    filterUpdateConditionAction
} from "../../../../redux/data/dataActions";

import {
    getDataColor
} from "../../../../utilities/ColorUtilities";

import {
    dataAddFilter,
    dataRemoveFilter
} from "../../../../redux/data/dataActions";
import {useTranslation} from "react-i18next";
import {getFieldAlias, getFieldFormatters} from "../../../../utilities/DataSourceUtilities";
import {formatValue} from "../../../../utilities/FormatUtilities";
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import useDimensions from "react-cool-dimensions";
import {findComponent} from "../../../../redux/page/pageActions";

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';

const PREFIX = 'BubbleChart';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    }
}));

export default function BubbleChart({id, configuration, xId, onLoading, onLoaded}) {

    const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const config = useSelector(state => state.config);
    const persist = useSelector(state => state.persist);
    const filters = useSelector(state => state.data.filters);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [chart, setChart] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [sliceValue, setSliceValue] = useState(-2112);
    const [noDataMessage, setNoDataMessage] = useState("No Data");
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);

	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const valueField = compConfig.dataSource?.fields?.valueField ? compConfig.dataSource?.fields?.valueField : '';
	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};
	const styles = compConfig?.element?.styles;

	const chartRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Bubble Chart Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'BubbleChart'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    useEffect(() => {
        // console.log('-----------------BUBBLE CHART---------STATUS->');
        // console.log('fetching: {0}'.format(dataSource.fetching));
        // console.log('fetched: {0}'.format(dataSource.fetched));
        // console.log('error: {0}'.format(dataSource.error));
        // console.log('data:', dataSource.data);

        if (dataSource.error) {
            setNoDataMessage("Error");
            setChartData([]);
        }
        else if (dataSource.data) {

            setNoDataMessage("No Data");
            let data = dataSource.data;

            // Update Data/Chart
            setChartData(data);
        }

    }, [dataSource.data, dataSource.error]);

    useEffect(() => {

        updateChart();

    }, [chartData, sliceValue, persist.themeLight]);

    useEffect(() => {
        const componentFilters = filters.find(f => {
            return f.id === id
        });
        if (!componentFilters && sliceValue !== -2112) {
            setSliceValue(-2112);
        }
    }, [filters]);


	useEffect( () => {
		if (chart) {
			// setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
			setTimeout(function() {
				chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
			}, 300)
			// console.log(ref.current.clientWidth);
		}
	}, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    const getSeriesFromGroupByField = (data) => {
        let series = [];
        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            const groupByValue = item[compConfig.dataSource.fields.groupByField];

            // HACK:
            // TODO: This needs to look up colors based on a data value (from config or db)??
            let color = config.data.ragStyle.noData;
            if (groupByValue.toLowerCase() === 'good') {
                color = config.data.ragStyle.green;
            } else if (groupByValue.toLowerCase() === 'normal') {
                color = config.data.ragStyle.amber;
            } else if (groupByValue.toLowerCase() === 'poor') {
                color = config.data.ragStyle.red;
            }

            let group = series.find(s => s.name === groupByValue);
            if (!group) {
                group = {
                    name: groupByValue,
                    color: color,
                    data: []
                }
                series.push(group);
            }

            const dataValue = item[compConfig.dataSource.fields.valueField];
            const dataValueName = item[compConfig.dataSource.fields.valueNameField];

            group.data.push({
                name: dataValueName,
                value: dataValue
            });
        }
        return series;
    }

    const getSeriesFromGroupByRange = (data) => {

        let series = [];
        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            const groupByValue = item[compConfig.dataSource.fields.groupByField];
            const ranges = compConfig.element.groupByRange.ranges;

            let itemGroup = compConfig.element.groupByRange.outOfRangeLabel;
            let itemColor = compConfig.element.groupByRange.outOfRangeColor;

            for (let r = 0; r < ranges.length; r++) {
                const floor = ranges[r].floor;
                const ceiling = ranges[r].ceiling;
                const label = ranges[r].label;
                const color = ranges[r].color;

                if (groupByValue >= floor && groupByValue < ceiling) {
                    itemGroup = label;
                    itemColor = color;
                    break;
                }
            }

            let group = series.find(s => s.name === itemGroup);
            if (!group) {
                group = {
                    name: itemGroup,
                    color: itemColor,
                    data: []
                }
                series.push(group);
            }

            const dataValue = item[compConfig.dataSource.fields.valueField];
            const dataValueName = item[compConfig.dataSource.fields.valueNameField];
            const dataO = {
                name: dataValueName,
                value: dataValue
            }

            // Add Ancillary Fields
            for (let a = 0; a < compConfig.dataSource.fields.ancillaryFields.length; a++) {
                const field = compConfig.dataSource.fields.ancillaryFields[a];
                dataO[field] = item[field];
            }

            group.data.push(dataO);
        }
        return series;
    }

    const getSeriesWithoutGrouping = (data) => {
        let seriesData = [];

        // HACK: NOTE: Carry over from SHE - this
        // expects comma separated data!
        // TODO: Resolve logic based on configuration

        // Setup series
        // console.log('> Setup series ...');
        // console.log('  - All values ...');
        // console.log('getSerieslWithoutGrouping', data);

        let allValues = [];
        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            // console.log('    > ITEM: ', item);
            if (item[valueField] !== null) {
                let itemPart = item[valueField].split(',');

                if (compConfig?.element?.splitCommaDelimitedValues) {
                    // console.log('    > ITEM PART: ', itemPart);
                    for (let j = 0; j < itemPart.length; j++) {
                        allValues.push(itemPart[j].trim());
                    }
                } else {
                    allValues.push();
                }
            }
        }
        // console.log('      > Array: ', allValues);

        const distinctValues = [...new Set(allValues)];
        // console.log('  - Distinct values ...');
        // console.log('      > Array: ', distinctValues);

        for (let k = 0; k < distinctValues.length; k++) {
            let count = 0
            for (let l = 0; l < allValues.length; l++) {
                if (distinctValues[k] === allValues[l]) {
                    count = count + 1;
                }
            }
            seriesData.push({
                'name': distinctValues[k],
                'value': count
            });
        }

        const series = [];
        series.push({
            type: 'packedbubble',
            name: valueField,
            data: seriesData
        });

        return series;
    }

    const updateChart = () => {

        // console.log('update chart');

        const data = chartData;

        if (!chart && data === null)
            return;

        if (chart && (data === null || data.length === 0)) {
            // No Data
            chart.update({
                plotOptions: {},
                series: []
            }, true, true);
            chart.showNoData();
            return;
        }

        // Supporting three types of data
        // 1) groupBy where the field has values i.e. groupByPhase
        // 2) groupBy where the groupBy field is then classified by ranges
        // 3) no groupBy - just use raw values OR if splitCommaDelimitedValues is true then values are exploded

        // Now we want to group the data by the type
        // and then display the value by type
        let series = [];
        if (compConfig.dataSource.fields.groupByField && !compConfig.element.groupByRange) {
            series = getSeriesFromGroupByField(data);
        } else if (compConfig.dataSource.fields.groupByField && compConfig.element.groupByRange) {
            series = getSeriesFromGroupByRange(data);
        } else {
            series = getSeriesWithoutGrouping(data);
        }

        // console.log('series', series);

        const chartOptions = {
            chart: {
                type: 'packedbubble',
                // animation: false
                // height: '100%',
                backgroundColor: null
            },
            credits: {
                enabled: false
            },
            // Card Attribution Used
            title: {
                text: '',
                margin: 0
            },
            subtitle: {
                text: '',
                margin: 0
            },
            lang: {
                noData: noDataMessage
            },
            noData: {
                style: {
                    fontWeight: 'bold',
                    fontSize: '15px',
                    color: '#303030'
                }
            },
            legend: {
                ...(highchartOptions.legend ? highchartOptions.legend : {}),
                itemStyle: {
                    color: '#727272',
                    fontWeight: 'bold'
                }
            },
            series: series,
            // dataLabels: {
            //     enabled: true,
            //     formatter: function() {
            //         console.log(' > BubbleChart: CREATE Chart: dataLabels.formatter(): ', this);
            //         return this.point.value;
            //     }
            // },
            tooltip: {
                ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                borderColor: '#000000',
                shared: true,
                useHTML: true,
                formatter: function(e) {
                    // console.log("e", e);
                    // console.log("this.point", this.point);

                    const seriesName = this.point?.name ? this.point?.name : this.series.name;
                    const count = this.point?.x;
                    const value = this.point?.y;

                    // TODO: Move up in scope
                    const label = getFieldAlias(dataSource, compConfig.dataSource.fields.valueField);
                    const fieldAlias = label !== null ? label : compConfig.dataSource.fields.valueField;
                    const valueFormatters = getFieldFormatters(dataSource, compConfig.dataSource.fields.valueField);
                    const formattedValue = formatValue(value, valueFormatters, null, true);

                    // console.log('value', value);
                    // console.log('count', count);
                    // console.log(formattedValue);

                    let formattedTooltip = '<table>';
                    formattedTooltip += "<tr><td colspan='2' style='font-weight: bold; text-align:center'>" + seriesName + "</td></tr>";

                    // TODO: Formatted vs css!!
                    formattedTooltip += "<tr><td style='text-align:left'>" + (value ? fieldAlias : "Count") +  ":</td><td style='text-align:right'><span style='font-weight: bold; " + formattedValue + "'>" + (value ? value : count) + "</td></tr>";

                    if (this.point.index && compConfig.dataSource.fields?.ancillaryFields?.length > 0) {
                        for (let a = 0; a < compConfig.dataSource.fields.ancillaryFields.length; a++) {
                            const field = compConfig.dataSource.fields.ancillaryFields[a];
                            // console.log('index', this.point.index);
                            // console.log('series.data', this.point.series.data);
                            // console.log('item', this.point.series.data[this.point.index]);
                            const fieldValue = this.point.series.data[this.point.index][field];
                            // console.log(fieldValue);
                            const label = getFieldAlias(dataSource, field);
                            const fieldAlias = label !== null ? label : field;
                            const valueFormatters = getFieldFormatters(dataSource, field);
                            const formattedValue = formatValue(fieldValue, valueFormatters, null);
                            // console.log(fieldValue);
                            // console.log(formattedValue);
                            // console.log(valueFormatters);
                            formattedTooltip += "<tr><td style='text-align:left'>{0}:</td><td style='text-align:right'><span style='font-weight:bold'>{1}</td></tr>".format(fieldAlias, formattedValue);
                        }
                    }

                    formattedTooltip += '</table>';
                    return formattedTooltip;
                }
            },
            plotOptions: {
                series: {
                    cursor: compConfig?.xFilter?.enabled ?  'pointer' : 'default',
                    point: {
                        events: {
                            click: function(e) {
                                // console.log('BubbleChart:SeriesClick', e);

                                // Are we supporting??
                                if (!compConfig?.xFilter?.enabled)
                                    return;

                                if (dataSource.fetching)
                                    return;

                                const count = e.point.x;
                                const value = e.point.y;
                                // console.log("Count: {0} Value: {1} Name: {2}".format(count, value, e.point.name));

                                // If we have a value then user clicked
                                // a detail bubble otherwise the category
                                // grouping
                                if (value) {
                                    // TODO: Build in more support for value/type etc??
                                    dispatch(dataAddFilter(id, compConfig.dataSource.fields.valueNameField, " eq '{0}'".format(e.point.name), filterUpdateConditionAction.REPLACE, true, false));
                                } else {
                                    const seriesLabel = e.point.series.name;
                                    const groupByField = compConfig.dataSource.fields.groupByField;
                                    const range = compConfig.element.groupByRange.ranges.find(r => r.label === seriesLabel);
                                    const floor = range.floor;
                                    const ceiling = range.ceiling;

                                    dispatch(dataAddFilter(id, groupByField, " gte {0}".format(floor), filterUpdateConditionAction.REPLACE, false, false));
                                    dispatch(dataAddFilter(id, groupByField, " lt {0}".format(ceiling), filterUpdateConditionAction.ADD, true, false));
                                }
                            }
                        }
                    },
                },
                packedbubble: {
                    ...highchartOptions?.plotOptions?.packedbubble,
                    // minSize: '20%',
                    // maxSize: '50%',
                    layoutAlgorithm: {
                        gravitationalConstant: 0.05,
                        splitSeries: true,
                        seriesInteraction: false,
                        // dragBetweenSeries: true,
                        // parentNodeLimit: true,
                        ...highchartOptions?.plotOptions?.packedbubble?.layoutAlgorithm,
                    },
                    dataLabels: {
                        format: '{point.name}',
                        style: {
                            color: 'black',
                            textOutline: 'none',
                            fontWeight: 'normal'
                        },
                        ...highchartOptions?.plotOptions?.packedbubble?.dataLabels,
                    },
                }
            }
        };

        if (chartRef.current) {
            if (!chart) {
                // console.log('** ---------- BUBBLE: CREATE Chart ----------**', chartOptions);

                setChart(Highcharts.chart(chartRef.current, chartOptions));
            } else {
                // console.log('** ---------- BUBBLE: UPDATE Chart ----------**', chartOptions);


                setChart(Highcharts.chart(chartRef.current, chartOptions));

                // NOTE: For some reason the bubble chart is not really happy
                // updating series producing a followPointer AND an appendChild
                // error
                // chart.update(chartOptions, true, true);
            }
        }
    };

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <div id={"bc"} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
                </CardContent>
            </Card>
        </Root>
    );
}
